import { z } from "zod";

import { severitySchema } from "../incidents/incidents";

/**
 * Controller I/O / Modbus channel contracts (ADR-081 / ADR-082).
 *
 * Mono-metric sensors do not use these types. Channel `key` values match the
 * Milesight UC300 official decoder field names.
 */

export const CHANNEL_KINDS = [
  "DIGITAL",
  "COUNTER",
  "DIGITAL_OUTPUT",
  "TEMPERATURE",
  "CURRENT_MA",
  "VOLTAGE",
  "MODBUS",
] as const;
export const channelKindSchema = z.enum(CHANNEL_KINDS);
export type ChannelKind = z.infer<typeof channelKindSchema>;

/** Template row used when materialising channels for a new controller. */
export interface HardwareChannelTemplate {
  key: string;
  kind: ChannelKind;
  unit?: string;
  sortOrder: number;
}

export const deviceChannelSchema = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  key: z.string().min(1),
  kind: channelKindSchema,
  label: z.string().nullable(),
  enabled: z.boolean(),
  unit: z.string().nullable(),
  sortOrder: z.number().int(),
  lastNumber: z.number().nullable(),
  lastBool: z.boolean().nullable(),
  lastText: z.string().nullable(),
  lastMeasuredAt: z.iso.datetime().nullable(),
  alertEnabled: z.boolean(),
  alertMin: z.number().nullable(),
  alertMax: z.number().nullable(),
  alertFaultHigh: z.boolean(),
  alertDurationSeconds: z.number().int().positive(),
  alertCooldownSeconds: z.number().int().nonnegative(),
  alertSeverity: severitySchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type DeviceChannel = z.infer<typeof deviceChannelSchema>;

export const channelReadingSchema = z.object({
  id: z.uuid(),
  channelId: z.uuid(),
  measuredAt: z.iso.datetime(),
  receivedAt: z.iso.datetime(),
  valueNumber: z.number().nullable(),
  valueBool: z.boolean().nullable(),
  valueText: z.string().nullable(),
});

export type ChannelReading = z.infer<typeof channelReadingSchema>;

/**
 * One channel sample extracted from a decoded uplink before persistence.
 * Not an HTTP DTO — part of NormalizedTelemetry.
 */
export const channelSampleSchema = z.object({
  key: z.string().min(1),
  kind: channelKindSchema,
  valueNumber: z.number().optional(),
  valueBool: z.boolean().optional(),
  valueText: z.string().optional(),
});

export type ChannelSample = z.infer<typeof channelSampleSchema>;

export const updateDeviceChannelRequestSchema = z
  .object({
    label: z.string().trim().max(120).nullable(),
    enabled: z.boolean(),
    unit: z.string().trim().max(32).nullable(),
    alertEnabled: z.boolean(),
    alertMin: z.number().nullable(),
    alertMax: z.number().nullable(),
    alertFaultHigh: z.boolean(),
    alertDurationSeconds: z.number().int().min(30).max(86_400),
    alertCooldownSeconds: z.number().int().min(0).max(604_800),
    alertSeverity: severitySchema,
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  })
  .refine(
    (value) =>
      value.alertMin === undefined ||
      value.alertMax === undefined ||
      value.alertMin === null ||
      value.alertMax === null ||
      value.alertMin < value.alertMax,
    { message: "alertMin must be less than alertMax.", path: ["alertMin"] },
  );

export type UpdateDeviceChannelRequest = z.infer<typeof updateDeviceChannelRequestSchema>;

/** Remote digital-output control for a UC300 DO channel (ADR-082). */
export const controlDeviceChannelRequestSchema = z.object({
  state: z.enum(["on", "off"]),
});

export type ControlDeviceChannelRequest = z.infer<typeof controlDeviceChannelRequestSchema>;

export const controlDeviceChannelResponseSchema = z.object({
  channelId: z.uuid(),
  key: z.string(),
  state: z.enum(["on", "off"]),
  /** Hex payload queued to the Network Server (Milesight UC300 format). */
  downlinkHex: z.string(),
  /** Whether the Network Server accepted the downlink. */
  queued: z.boolean(),
  detail: z.string().optional(),
});

export type ControlDeviceChannelResponse = z.infer<typeof controlDeviceChannelResponseSchema>;
